import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar } from "@/components/shared/Avatar";
import { isAssignableUser, useUsers } from "@/hooks/use-users";
import { useSalesUnits } from "@/hooks/use-sales-units";
import { useCustomerOwners, useSetCustomerOwners } from "@/hooks/use-customer-owners";

interface CustomerOwnersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
}

/**
 * Endre kundeeiere: velg brukere direkte, eller en salgsenhet som fan-outer til
 * medlemmene (som project_owners). Hele settet byttes via RPC set_customer_owners,
 * som logger forrige og nytt sett.
 */
export default function CustomerOwnersDialog({ open, onOpenChange, customerId }: CustomerOwnersDialogProps) {
  const { data: users = [] } = useUsers();
  const { data: owners = [] } = useCustomerOwners(customerId);
  const { data: units = [] } = useSalesUnits(new Date().getFullYear());
  const setOwners = useSetCustomerOwners();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [unitId, setUnitId] = useState<string | null>(null);

  const candidates = useMemo(() => users.filter(isAssignableUser), [users]);
  // Bare aktive ansatte kan være eiere (RPC-en avviser resten), så inaktive eiere sluttes ut her.
  const candidateIds = useMemo(() => new Set(candidates.map((u) => u.id)), [candidates]);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(owners.map((o) => o.user_id).filter((id) => candidateIds.has(id))));
    setUnitId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId, owners.length, candidateIds.size]);

  const toggle = (userId: string, checked: boolean) => {
    setUnitId(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(userId);
      else next.delete(userId);
      return next;
    });
  };

  const pickUnit = (id: string) => {
    const unit = units.find((u) => u.id === id);
    if (!unit) return;
    setUnitId(id);
    setSelected(new Set((unit.members ?? []).map((m) => m.user_id).filter((uid) => candidateIds.has(uid))));
  };

  const save = async () => {
    await setOwners.mutateAsync({ customerId, userIds: [...selected].filter((uid) => candidateIds.has(uid)), salesUnitId: unitId });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Endre eiere</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {units.length > 0 && (
            <div className="space-y-1.5">
              <Label htmlFor="owners-unit">Velg salgsenhet (setter alle medlemmer som eiere)</Label>
              <Select value={unitId ?? ""} onValueChange={pickUnit}>
                <SelectTrigger id="owners-unit" aria-label="Velg salgsenhet">
                  <SelectValue placeholder="Salgsenhet" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium mb-1.5">Eiere</legend>
            {candidates.map((u) => (
              <label key={u.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
                <Checkbox
                  checked={selected.has(u.id)}
                  onCheckedChange={(c) => toggle(u.id, c === true)}
                  aria-label={u.full_name}
                />
                <Avatar name={u.full_name} avatarUrl={u.avatar_url} size="sm" />
                {u.full_name}
              </label>
            ))}
          </fieldset>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button type="button" onClick={() => save().catch(() => undefined)} disabled={setOwners.isPending} aria-label="Lagre eiere">
            Lagre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
